import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LegalShell = ({ title, eyebrow = "Legal", children, testid }) => (
  <div className="min-h-screen bg-[#F4F8F8] text-neutral-900" data-testid={testid}>
    <div className="mx-auto max-w-3xl px-6 py-16">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A8B85] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Back to home
      </Link>
      <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-[#0A8B85]">
        {eyebrow}
      </p>
      <h1
        className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl"
        style={{ fontFamily: "Manrope, sans-serif" }}
      >
        {title}
      </h1>
      <div className="mt-2 text-sm text-neutral-500">
        Last updated: February 2026
      </div>
      <div className="mt-10 space-y-5 text-[15px] leading-relaxed text-neutral-700 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-neutral-900 [&_h2]:tracking-tight [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_a]:text-[#0A8B85] [&_a]:underline [&_a:hover]:no-underline [&_strong]:text-neutral-900">
        {children}
      </div>
    </div>
  </div>
);

export const Privacy = () => (
  <LegalShell title="Privacy Policy" testid="page-privacy">
    <p>
      Clinq (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;) is committed
      to protecting the privacy of every business owner, funder, and visitor on
      our platform. This Privacy Policy explains what we collect, why we
      collect it, and the rights you have.
    </p>

    <h2>1. Information we collect</h2>
    <ul>
      <li>
        <strong>Account data</strong> — name, business name, email, role, and
        password hash.
      </li>
      <li>
        <strong>Company data</strong> — sector, country, stage, ticket range,
        revenue, employees, SDGs, value proposition, and any documents you
        upload (pitch decks, financial models, management accounts, legal
        documents).
      </li>
      <li>
        <strong>Usage data</strong> — page views, deal views, document
        downloads, audit-trail events, and authenticated session metadata.
      </li>
      <li>
        <strong>Cookies & tokens</strong> — strictly necessary cookies for
        authentication.
      </li>
    </ul>

    <h2>2. How we use it</h2>
    <ul>
      <li>To match businesses with mandate-aligned funders.</li>
      <li>
        To compute funder-readiness scores and surface AI insights to you.
      </li>
      <li>To send transactional emails (account, NDA, application updates).</li>
      <li>
        To meet legal, regulatory, and audit obligations of the platform.
      </li>
    </ul>

    <h2>3. Sharing</h2>
    <p>
      Your sensitive company documents are <strong>never</strong> shared with a
      funder without your explicit in-platform approval. When you approve an
      funder interest, the funder is granted scoped, audit-logged access to
      your data room.
    </p>

    <h2>4. Data retention</h2>
    <p>
      We retain account and company data while your account is active. You can
      request deletion at any time by writing to{" "}
      <a href="mailto:hello@clinq.africa">hello@clinq.africa</a>.
    </p>

    <h2>5. Security</h2>
    <p>
      Passwords are hashed with bcrypt. All traffic is encrypted in transit.
      Document access is gated by NDA acceptance and audit-logged.
    </p>

    <h2>6. Your rights</h2>
    <p>
      You have the right to access, rectify, port, and delete your data, and
      to object to processing. Contact{" "}
      <a href="mailto:hello@clinq.africa">hello@clinq.africa</a>.
    </p>

    <h2>7. Contact</h2>
    <p>
      Questions about this policy? Email{" "}
      <a href="mailto:hello@clinq.africa">hello@clinq.africa</a>.
    </p>
  </LegalShell>
);

export const Terms = () => (
  <LegalShell title="Terms of Service" testid="page-terms">
    <p>
      By using the Clinq platform you agree to these terms. If you do not
      agree, please do not use Clinq.
    </p>

    <h2>1. The Clinq platform</h2>
    <p>
      Clinq is a technology platform that connects African SMEs with
      institutional funders. We are <strong>not</strong> a broker-dealer, do
      not provide investment, legal, or tax advice, and are not a party to any
      transaction between a business and a funder.
    </p>

    <h2>2. Eligibility</h2>
    <p>
      You must have authority to bind the business or fund you sign up on
      behalf of, and to upload the information you provide.
    </p>

    <h2>3. Acceptable use</h2>
    <ul>
      <li>No misrepresentation of your business, fund, or identity.</li>
      <li>No scraping, redistribution, or off-platform circumvent.</li>
      <li>No upload of content you do not have rights to.</li>
    </ul>

    <h2>4. Fees</h2>
    <p>
      Founders may use the platform&apos;s core onboarding and matching at no cost. Optional
      paid services — including the detailed AI readiness report (CCR Score™ unlock) and
      add-on services — are clearly priced before purchase. Funders subscribe to paid tiers —
      see <Link to="/login">funder billing</Link> for details.
    </p>

    <h2>5. Termination</h2>
    <p>
      We may suspend or terminate any account that violates these terms or our
      NDA &amp; Non-circumvent Agreement.
    </p>

    <h2>6. Governing law</h2>
    <p>
      These terms are governed by the laws of the Republic of Kenya. Disputes
      go to mediation in Nairobi and then arbitration under NCIA Rules.
    </p>

    <h2>7. Contact</h2>
    <p>
      Questions? Email{" "}
      <a href="mailto:hello@clinq.africa">hello@clinq.africa</a>.
    </p>
  </LegalShell>
);

export const NDA = () => (
  <LegalShell title="NDA &amp; Non-Circumvent" testid="page-nda">
    <p>
      Every business owner accepts our Mutual Non-Disclosure &amp;
      Non-Circumvent Agreement before any document can be viewed by an
      funder. The full agreement is presented and signed in-platform when
      you onboard.
    </p>
    <ul>
      <li>
        Your confidential information is <strong>never</strong> shared with a
        funder without your explicit pre-approval.
      </li>
      <li>
        Funder identities are anonymised in interest notifications until you
        approve.
      </li>
      <li>
        A 24-month non-circumvent clause protects both parties from
        off-platform deal flow.
      </li>
      <li>
        Jurisdiction: Republic of Kenya. Disputes go to mediation in Nairobi
        and then arbitration under NCIA Rules.
      </li>
    </ul>
    <p>
      To request the latest agreement, email{" "}
      <a href="mailto:hello@clinq.africa">hello@clinq.africa</a>.
    </p>
  </LegalShell>
);

export default Privacy;
